import {isFeatureEnabled} from '@github-ui/feature-flags'
import {md2html} from '@github-ui/md2html'
import {DOCUMENT_FIELDS_PATH, type SetFieldsOperation} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {useMutation} from '@github-ui/react-query'
import {useCallback} from 'react'
import type {Disposable, IEnvironment, RecordSourceProxy} from 'relay-runtime'

import {updateIssueBody, type UpdateIssueBodyInput, type UpdateIssueBodyIssue} from './api-update-issue-body'
import {reportAsyncError} from './report-async-error'
import {commitUpdateIssueBodyMutation} from './update-issue-body-mutation'

export type UpdateIssueBodyCompletion = {
  readonly updateIssue: {
    readonly issue: {readonly bodyVersion: string} | null
  } | null
}

type CommitUpdateIssueBodyOptions = {
  input: {issueId: string; body: string; bodyVersion: string}
  withOptimisticResponse?: boolean
  onCompleted?: (response: UpdateIssueBodyCompletion) => void
  onError?: (error: Error) => void
}

function toIssueBodyOperation(issue: UpdateIssueBodyIssue): SetFieldsOperation {
  return {
    type: 'setFields',
    id: issue.id,
    fields: {
      body: issue.body,
      bodyHTML: issue.bodyHTML,
      bodyVersion: issue.bodyVersion,
      lastEditedAt: issue.lastEditedAt,
      viewerCanReadUserContentEdits: issue.viewerCanReadUserContentEdits,
      lastUserContentEdit: issue.lastUserContentEdit,
    },
  }
}

function inputFromOperation(operation: SetFieldsOperation): UpdateIssueBodyInput {
  const {body, bodyVersion} = operation.fields
  if (typeof operation.id !== 'string' || typeof body !== 'string' || typeof bodyVersion !== 'string') {
    throw new Error('Invalid issue body operation')
  }
  return {id: operation.id, body, bodyVersion}
}

function optimisticIssueFromOperation(operation: SetFieldsOperation): UpdateIssueBodyIssue {
  const {body, bodyHTML, bodyVersion, lastEditedAt, viewerCanReadUserContentEdits, lastUserContentEdit} =
    operation.fields
  if (
    typeof operation.id !== 'string' ||
    typeof body !== 'string' ||
    typeof bodyHTML !== 'string' ||
    typeof bodyVersion !== 'string' ||
    typeof lastEditedAt !== 'string' ||
    typeof viewerCanReadUserContentEdits !== 'boolean' ||
    lastUserContentEdit !== null
  ) {
    throw new Error('Invalid optimistic issue body operation')
  }
  return {
    id: operation.id,
    body,
    bodyHTML,
    bodyVersion,
    lastEditedAt,
    viewerCanReadUserContentEdits,
    lastUserContentEdit,
  }
}

function updateRelayIssue(store: RecordSourceProxy, issue: UpdateIssueBodyIssue) {
  const record = store.get(issue.id)
  if (!record) return

  record.setValue(issue.body, 'body')
  record.setValue(issue.bodyHTML, 'bodyHTML', {unfurlReferences: true})
  record.setValue(issue.bodyVersion, 'bodyVersion')
  record.setValue(issue.lastEditedAt, 'lastEditedAt')
  record.setValue(issue.viewerCanReadUserContentEdits, 'viewerCanReadUserContentEdits')

  const lastEdit = issue.lastUserContentEdit
  if (!lastEdit) {
    record.setValue(null, 'lastUserContentEdit')
    return
  }

  const editRecord = store.get(lastEdit.id) ?? store.create(lastEdit.id, 'UserContentEdit')
  editRecord.setValue(lastEdit.id, 'id')
  const editor = lastEdit.editor
  if (!editor) {
    editRecord.setValue(null, 'editor')
  } else {
    const editorRecord = store.get(editor.id) ?? store.create(editor.id, editor.__typename)
    editorRecord.setValue(editor.id, 'id')
    editorRecord.setValue(editor.url, 'url')
    editorRecord.setValue(editor.login, 'login')
    editRecord.setLinkedRecord(editorRecord, 'editor')
  }
  record.setLinkedRecord(editRecord, 'lastUserContentEdit')
}

function errorFromUnknown(error: unknown): Error {
  return error instanceof Error ? error : new Error('Failed to update issue body')
}

export function useUpdateIssueBodyMutation({
  environment,
  owner,
  repo,
  issueNumber,
}: {
  environment: IEnvironment
  owner: string
  repo: string
  issueNumber: number
}) {
  const {mutateAsync: mutateOptimistically} = useEntityMutation<UpdateIssueBodyIssue, Error, Disposable>({
    mutationFn: operation => updateIssueBody({owner, repo, issueNumber, input: inputFromOperation(operation)}),
    getConfirmedOperation: issue => toIssueBodyOperation(issue),
    onMutate: ({operation}) =>
      environment.applyUpdate({
        storeUpdater: store => updateRelayIssue(store, optimisticIssueFromOperation(operation)),
      }),
    onError: (_error, _variables, optimisticUpdate) => optimisticUpdate?.dispose(),
    onSuccess: async (issue, _variables, optimisticUpdate) => {
      environment.commitUpdate(store => updateRelayIssue(store, issue))
      optimisticUpdate?.dispose()
    },
  })

  const {mutateAsync: mutateWithoutOptimism} = useMutation<UpdateIssueBodyIssue, Error, UpdateIssueBodyInput>({
    mutationFn: input => updateIssueBody({owner, repo, issueNumber, input}),
    onSuccess: async issue => {
      environment.commitUpdate(store => updateRelayIssue(store, issue))
      await notifySettlement(toIssueBodyOperation(issue), ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
    },
  })

  return useCallback(
    ({input, withOptimisticResponse = false, onCompleted, onError}: CommitUpdateIssueBodyOptions) => {
      if (!isFeatureEnabled('issue_viewer_update_body_tanstack_mutation')) {
        return commitUpdateIssueBodyMutation({
          environment,
          input,
          withOptimisticResponse,
          onCompleted: response => {
            const issue = response.updateIssue?.issue
            onCompleted?.({
              updateIssue: response.updateIssue ? {issue: issue ? {bodyVersion: issue.bodyVersion} : null} : null,
            })
          },
          onError,
        })
      }

      const nativeInput = {id: input.issueId, body: input.body, bodyVersion: input.bodyVersion}
      const commit = async () => {
        let issue: UpdateIssueBodyIssue
        try {
          issue = withOptimisticResponse
            ? await mutateOptimistically({
                operation: toIssueBodyOperation({
                  ...nativeInput,
                  bodyHTML: md2html(input.body),
                  lastEditedAt: new Date().toISOString(),
                  viewerCanReadUserContentEdits: true,
                  lastUserContentEdit: null,
                }),
              })
            : await mutateWithoutOptimism(nativeInput)
        } catch (error) {
          onError?.(errorFromUnknown(error))
          return
        }

        // The write and store update have already committed. Run the consumer success callback
        // outside the network/cache try boundary so a throwing onCompleted is surfaced to the
        // global error handler rather than routed to onError and misreported as a failed mutation.
        try {
          onCompleted?.({updateIssue: {issue: {bodyVersion: issue.bodyVersion}}})
        } catch (error) {
          reportAsyncError(error)
        }
      }

      void commit()
    },
    [environment, mutateOptimistically, mutateWithoutOptimism],
  )
}
